/**
 * Cliente WebSocket com reconexão automática.
 * Endereço relativo (mesma origem) com fallback para o host atual.
 */
import { useEffect, useState } from 'react';

export interface SnapshotMessage {
  type: 'snapshot';
  payload: {
    ts: number;
    agents: Array<{
      id: string;
      name: string;
      hostname: string | null;
      online: boolean;
      lastSeenAt: number | null;
      cpuPct: number | null;
      memPct: number | null;
      load1: number | null;
      ts: number | null;
    }>;
  };
}

export interface AgentsMessage {
  type: 'agents';
  payload: { agents: Array<{ id: string; name: string; hostname: string | null; os: string | null; lastSeenAt: number | null; online: boolean }> };
}

export type WsMessage = SnapshotMessage | AgentsMessage;

type Listener = (msg: WsMessage) => void;

class WsClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private reconnectTimer: number | null = null;
  private closed = false;

  start(): void {
    if (this.ws) return;
    this.closed = false;
    this.connect();
  }

  stop(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    this.start();
    return () => {
      this.listeners.delete(fn);
      if (this.listeners.size === 0) this.stop();
    };
  }

  private connect(): void {
    if (this.closed) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/ws/overview`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      // Conexão estabelecida.
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as WsMessage;
        for (const l of this.listeners) l(msg);
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      this.ws = null;
      if (!this.closed) {
        this.reconnectTimer = window.setTimeout(() => this.connect(), 3000);
      }
    };
    ws.onerror = () => {
      try { ws.close(); } catch { /* ignore */ }
    };
  }
}

export const wsClient = new WsClient();

/** Hook que assina snapshots de overview em tempo real. */
export function useOverviewWs(): { snapshot: SnapshotMessage['payload'] | null; connected: boolean } {
  const [snapshot, setSnapshot] = useState<SnapshotMessage['payload'] | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let lastValues: SnapshotMessage['payload'] | null = null;
    const unsub = wsClient.subscribe((msg) => {
      if (msg.type === 'snapshot') {
        lastValues = msg.payload;
        setSnapshot(msg.payload);
        setConnected(true);
      }
    });
    return () => {
      unsub();
      void lastValues;
    };
  }, []);

  return { snapshot, connected };
}
